import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { App, Button, Card, Descriptions, Input, InputNumber, Space, Switch, Tag, Typography } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { usePermissions } from '../../auth/store';
import {
  configError,
  DEFAULT_CONFIG,
  formatSeconds,
  minuteToTime,
  timeToMinute,
  type CallLimitConfig,
  type NightLimitUser,
} from './callLimits';

const KEY = ['admin', 'call-limits'];

function errMsg(e: unknown, fallback: string) {
  return (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? fallback;
}

/**
 * Hidden call-limit settings — reachable only by URL (`/call-limits`) and only
 * for super admins. Any other admin is sent to the dashboard exactly like an
 * unknown URL, and the API refuses them regardless.
 */
export function CallLimitsPage() {
  const { role } = usePermissions();
  if (role !== 'super_admin') return <Navigate to="/" replace />;
  return <CallLimitsContent />;
}

function CallLimitsContent() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const { data } = useQuery({
    queryKey: KEY,
    queryFn: async () => (await api.get<CallLimitConfig>('/admin/call-limits')).data,
  });
  const [form, setForm] = useState<CallLimitConfig>(DEFAULT_CONFIG);
  useEffect(() => {
    if (data) setForm(data);
  }, [data]);
  const set = (patch: Partial<CallLimitConfig>) => setForm((f) => ({ ...f, ...patch }));
  const problem = configError(form);

  const save = useMutation({
    mutationFn: async () => (await api.post<CallLimitConfig>('/admin/call-limits', form)).data,
    onSuccess: (saved) => {
      qc.setQueryData(KEY, saved);
      message.success('Call limits saved');
    },
    onError: (e) => message.error(errMsg(e, 'Could not save the call limits')),
  });

  return (
    <Space direction="vertical" size={16} style={{ width: '100%', maxWidth: 760 }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Call limits
        </Typography.Title>
        <Typography.Text type="secondary">Super admins only. This page is not in the menu.</Typography.Text>
      </div>

      <Card title="Admin verification call">
        <Space direction="vertical">
          <Typography.Text>Auto-disconnect a verification call after</Typography.Text>
          <Space>
            <InputNumber
              aria-label="Verification call length (seconds)"
              min={30}
              max={3600}
              step={30}
              value={form.verificationMaxSec}
              onChange={(v) => set({ verificationMaxSec: Number(v ?? 0) })}
            />
            <Typography.Text>seconds</Typography.Text>
            <Typography.Text type="secondary">= {formatSeconds(form.verificationMaxSec)}</Typography.Text>
          </Space>
        </Space>
      </Card>

      <Card
        title="Night call limit"
        extra={
          <Switch
            aria-label="Night limit on"
            checked={form.nightEnabled}
            onChange={(v) => set({ nightEnabled: v })}
          />
        }
      >
        <Space direction="vertical" size={10}>
          <Typography.Text type="secondary">
            Limited users (switched on below, or anyone who ever got coins from an admin wallet adjust) can talk
            this many minutes in total during the night window, even with coins. At the limit the call
            disconnects and new calls are refused until the window closes.
          </Typography.Text>
          <Space wrap>
            <InputNumber
              aria-label="Night minutes"
              min={1}
              max={600}
              value={form.nightMinutes}
              onChange={(v) => set({ nightMinutes: Number(v ?? 0) })}
              disabled={!form.nightEnabled}
            />
            <span>minutes</span>
            <span>from</span>
            <Input
              aria-label="Night starts"
              type="time"
              style={{ width: 130 }}
              value={minuteToTime(form.nightStartMinute)}
              onChange={(e) => {
                const m = timeToMinute(e.target.value);
                if (m !== null) set({ nightStartMinute: m });
              }}
              disabled={!form.nightEnabled}
            />
            <span>to</span>
            <Input
              aria-label="Night ends"
              type="time"
              style={{ width: 130 }}
              value={minuteToTime(form.nightEndMinute)}
              onChange={(e) => {
                const m = timeToMinute(e.target.value);
                if (m !== null) set({ nightEndMinute: m });
              }}
              disabled={!form.nightEnabled}
            />
            <Typography.Text type="secondary">IST</Typography.Text>
          </Space>
        </Space>
      </Card>

      <Space>
        <Button type="primary" disabled={problem !== null} loading={save.isPending} onClick={() => save.mutate()}>
          Save
        </Button>
        {problem && <Typography.Text type="danger">{problem}</Typography.Text>}
      </Space>

      <NightLimitUserCard />
    </Space>
  );
}

/** Look up one user and switch their night limit on or off. */
function NightLimitUserCard() {
  const { message } = App.useApp();
  const [ref, setRef] = useState('');
  const [user, setUser] = useState<NightLimitUser | null>(null);

  const lookup = useMutation({
    mutationFn: async (r: string) =>
      (await api.get<NightLimitUser>(`/admin/call-limits/users/${encodeURIComponent(r)}`)).data,
    onSuccess: setUser,
    onError: (e) => {
      setUser(null);
      message.error(errMsg(e, 'User not found'));
    },
  });
  const toggle = useMutation({
    mutationFn: async (enabled: boolean) =>
      (await api.post<NightLimitUser>(`/admin/call-limits/users/${user?.id}/night-limit`, { enabled })).data,
    onSuccess: (u) => {
      setUser(u);
      message.success(u.nightCallLimited ? 'Night limit switched on' : 'Night limit switched off');
    },
    onError: (e) => message.error(errMsg(e, 'Could not change the night limit')),
  });

  return (
    <Card title="Limit a user">
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Input.Search
          aria-label="User ID or handle"
          placeholder="User ID or @handle"
          enterButton="Find"
          value={ref}
          onChange={(e) => setRef(e.target.value)}
          onSearch={(v) => v.trim() && lookup.mutate(v.trim())}
          loading={lookup.isPending}
        />
        {user && (
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="User">
              {user.displayName ? `${user.displayName} ` : ''}@{user.handle}
              {user.isVerificationAccount && <Tag style={{ marginLeft: 8 }}>Verification account</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="Night limit switch">
              <Switch
                aria-label="Night limit for this user"
                checked={user.nightCallLimited}
                loading={toggle.isPending}
                onChange={(v) => toggle.mutate(v)}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Got coins from an admin">
              {user.adminCredited ? <Tag color="orange">Yes, so always limited</Tag> : 'No'}
            </Descriptions.Item>
            <Descriptions.Item label="Limited">
              {user.limited ? <Tag color="red">Yes</Tag> : <Tag>No</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="Tonight">
              {user.tonight.windowOpen
                ? `${formatSeconds(user.tonight.usedSec)} used of ${formatSeconds(user.tonight.limitSec)}`
                : 'Night window not open now'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Space>
    </Card>
  );
}
